import { lazy, Suspense } from 'react';
import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import { Container, Spinner } from 'react-bootstrap';
import { LandingPage } from './pages/LandingPage.js';
import { RequireAuth } from './auth/RequireAuth.js';
import { RequireCapability } from './auth/RequireCapability.js';
import { SelectProducerPage } from './auth/SelectProducerPage.js';
import { SelectRolePage } from './auth/SelectRolePage.js';
import { NoProducerPage } from './auth/NoProducerPage.js';
import { AuthErrorPage } from './auth/AuthErrorPage.js';
import { ROUTE } from './lib/routes.js';

// Module routes — lazy-loaded so unauthenticated users don't download
// internal code (STRUCTURE.md D2 bundle hygiene). Each module owns one
// URL prefix; nothing lives at the root catch-all anymore.
const QuoterRoutes = lazy(() => import('./quoter/QuoterRoutes.js'));
const EAppRoutes = lazy(() => import('./e-app/EAppRoutes.js'));
const PortalRoutes = lazy(() => import('./portal/PortalRoutes.js'));
const UwRoutes = lazy(() => import('./uw/UwRoutes.js'));
// Product Owner module — moved from ./admin/ to ./product-owner/ in the
// URL rename. The internal component is still named `AdminRoutes`
// (renaming component names is a separate follow-up if desired).
const ProductOwnerRoutes = lazy(() => import('./product-owner/AdminRoutes.js'));
const RegionalManagerRoutes = lazy(() => import('./regional-manager/RegionalManagerRoutes.js'));
const TpaUwRoutes = lazy(() => import('./tpa-uw/TpaUwRoutes.js'));
const NewBusinessRoutes = lazy(() => import('./new-business/NewBusinessRoutes.js'));
const AdministratorRoutes = lazy(() => import('./admin/AdministratorRoutes.js'));
const ClaimAdminRoutes = lazy(() => import('./claim-admin/ClaimAdminRoutes.js'));

function PageLoader() {
  return (
    <Container className="py-5 text-center">
      <Spinner animation="border" role="status" />
    </Container>
  );
}

// BLUF-1404 reclaimed `/admin/*` as the Administrator module mount.
// The BLUF-1393 `AdminToProductOwnerRedirect` — a blanket forward
// from `/admin/*` → `/product-owner/*` — has been removed: Product
// Owner sessions land on `/product-owner/*` directly (their
// `[landing:]` in the role catalogue is updated), and Administrator
// sessions mount `/admin/*` for real (see the route below). Any
// stale ProductOwners bookmark pointing at `/admin/*` now hits
// `RequireCapability(admin-view)` and lands on AccessDeniedPage; a
// re-login refreshes the session's landing to `/product-owner/`.

/**
 * BLUF-1404 AC-N4 — transitional `/administrator/*` → `/admin/*`
 * redirect. BLUF-1085 pre-provisioned the Administrator module at
 * `/administrator/*` as a placeholder; BLUF-1404 flipped the mount
 * to `/admin/*`. Any cached Cognito session landing, external
 * bookmark, or old email link that still points at
 * `/administrator/…` gets forwarded here so it lands on the real
 * URL instead of a blank page. Preserves path suffix + query
 * string. Drop this shim after ~2 release cycles once sessions
 * have naturally cycled and downstream links have been refreshed.
 */
function AdministratorToAdminRedirect() {
  const location = useLocation();
  const suffix = location.pathname.replace(/^\/administrator/, '');
  return <Navigate to={`/admin${suffix}${location.search}`} replace />;
}

/**
 * BLUF-1446 — transitional `/manager/*` → `/regional-manager/*`
 * redirect. Cached Cognito landings from the placeholder
 * `/manager/dashboard` era route through this shim to the
 * Regional Manager landing page.
 *
 * Unlike AdministratorToAdminRedirect (which preserves the suffix
 * because most `/admin/*` sub-routes exist), the new Regional
 * Manager module does NOT have a `/dashboard` route — the only
 * suffix cached in the wild (`/manager/dashboard`) has no 1:1
 * mapping. Preserving the suffix verbatim would land those users
 * on a 404 / blank page. So the shim always routes to the module
 * landing (`/regional-manager/application-search`) and drops the
 * old suffix; the query string is still preserved for any URL
 * params callers appended.
 */
function ManagerToRegionalManagerRedirect() {
  const location = useLocation();
  return <Navigate to={`${ROUTE.REGIONAL_MANAGER_APPLICATION_SEARCH}${location.search}`} replace />;
}

// Module bundles gate on capability tags parsed from the caller's
// Cognito group description on the server side (see
// `api/src/auth/role-catalogue.ts` and `GET /api/me`). Grant a role
// access to a module by editing the Cognito group description
// (`[caps: uw-view, ...]`), not by editing this file.
//
// The API's route pre-handlers hold the authoritative authorization;
// these UI guards short-circuit the lazy import so unauthorized users
// don't download restricted code.
const CAP_ADVISOR = 'role:advisor';
// `uw-portal-view` gates the UW module UI. Distinct from `uw-view`
// (which is the API-level read cap held by any role that legitimately
// needs to read UW-scoped data — e.g. New Business reads the queue
// through their own NB-scoped Application Search / Policy Admin pages
// via `/api/uw/queue` and `/api/uw/policies`). Only the real UW roles
// (Underwriters, ThirdPartyUnderwriters, UnderwritingAssistants,
// TeleInterviewers, ProductOwners) should ever see the UW portal chrome, so
// the UI mount uses this stricter cap to keep New Business bounced at
// the /uw/* boundary.
const CAP_UW = 'uw-portal-view';
// BLUF-1444 O8 — URL-space mounts use `role:<name>` action grants
// from the new-shape permission grammar. Each token is held by
// exactly one Cognito group; grant / revoke = Description edit.
// `/product-owner/*` is disjoint from `/admin/*` — a session that
// holds only one of these tokens sees exactly one tree.
const CAP_PRODUCT_OWNER = 'role:product-owner';
const CAP_ADMIN = 'role:administrator';
// BLUF-1446 — Regional Manager URL-space (5 read-only pages, no
// mutations). Held by the InternalRegionalManagers and
// ExternalRegionalManagers Cognito groups.
const CAP_REGIONAL_MANAGER = 'role:regional-manager';
// UW-family sub-role tags. These stay flat (auth-flow flags per
// permissions.ts:FLAT_LEGACY_CAPS) because each is a URL-space
// bundle guard for one specific placeholder dashboard, not a
// role-identity claim in the role:<name> sense.
const CAP_TPA_UW = 'tpa-uw-view';
const CAP_NEW_BUSINESS = 'role:new-business';
// BLUF-1395 — Claim Administrator role. Gates `/claim-admin/*` only;
// the policy-search endpoint is gated server-side on
// `policies:read:all`.
const CAP_CLAIM_ADMIN = 'role:claim-admin';

/**
 * Top-level router. The Landing page sits at the root; module routes
 * are lazy-mounted underneath. Cognito's redirect target is now
 * `/api/auth/callback` (handled by the API), not a UI page.
 */
function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      {/*
        BLUF-1404 — `/admin/*` is the Administrator module. Direct
        mount (no redirect), gated on `admin-view`. Any ProductOwners
        session hitting a stale /admin/* bookmark lands on
        AccessDeniedPage until they re-authenticate with the updated
        `[landing:]`.
      */}
      {/*
        BLUF-1404 AC-N4 — transitional redirect for the BLUF-1085
        placeholder URL. Delete once sessions have cycled through
        the new Cognito `[landing:]` values.
      */}
      <Route path="/administrator/*" element={<AdministratorToAdminRedirect />} />
      <Route path="/auth/select-producer" element={<SelectProducerPage />} />
      <Route path="/auth/select-role" element={<SelectRolePage />} />
      <Route path="/auth/no-producer" element={<NoProducerPage />} />
      <Route path="/auth/error" element={<AuthErrorPage />} />
      <Route
        path="/eapp/*"
        element={
          <RequireAuth>
            <RequireCapability capability={CAP_ADVISOR}>
              <Suspense fallback={<PageLoader />}>
                <EAppRoutes />
              </Suspense>
            </RequireCapability>
          </RequireAuth>
        }
      />
      <Route
        path="/portal/*"
        element={
          <RequireAuth>
            <RequireCapability capability={CAP_ADVISOR}>
              <Suspense fallback={<PageLoader />}>
                <PortalRoutes />
              </Suspense>
            </RequireCapability>
          </RequireAuth>
        }
      />
      <Route
        path="/uw/*"
        element={
          <RequireAuth>
            <RequireCapability capability={CAP_UW}>
              <Suspense fallback={<PageLoader />}>
                <UwRoutes />
              </Suspense>
            </RequireCapability>
          </RequireAuth>
        }
      />
      <Route
        path="/regional-manager/*"
        element={
          <RequireAuth>
            <RequireCapability capability={CAP_REGIONAL_MANAGER}>
              <Suspense fallback={<PageLoader />}>
                <RegionalManagerRoutes />
              </Suspense>
            </RequireCapability>
          </RequireAuth>
        }
      />
      {/*
        BLUF-1446 — transitional `/manager/*` → `/regional-manager/*`
        redirect for cached Cognito sessions still shipping
        `/manager/dashboard` as their landing. Mirrors BLUF-1404's
        `/administrator/*` → `/admin/*` shim. Drop once sessions
        have cycled through the new `[landing:]`.
      */}
      <Route path="/manager/*" element={<ManagerToRegionalManagerRedirect />} />
      <Route
        path="/tpa-uw/*"
        element={
          <RequireAuth>
            <RequireCapability capability={CAP_TPA_UW}>
              <Suspense fallback={<PageLoader />}>
                <TpaUwRoutes />
              </Suspense>
            </RequireCapability>
          </RequireAuth>
        }
      />
      {/* BLUF-1542 — `/uw-assistant/*` retired. UW Assistant now
          shares the `/uw/*` surface with every other UW-family role;
          the sidebar shape is derived from caps in `getUwNav`. Any
          legacy `/uw-assistant/...` link redirects to the UW dashboard
          via the /uw-assistant/* → /uw/underwriter-dashboard route
          below. */}
      <Route
        path="/uw-assistant/*"
        element={<Navigate to="/uw/underwriter-dashboard" replace />}
      />
      {/* BLUF-1543 — `/tele-interview/*` retired. Tele Interviewer
          shares the `/uw/*` surface with every other UW-family role;
          `getUwNav(caps)` widens the sidebar to the TI/UA shape when
          `tele-interview-view` is present. Legacy path redirects to
          the UW dashboard for backwards compatibility. */}
      <Route
        path="/tele-interview/*"
        element={<Navigate to="/uw/underwriter-dashboard" replace />}
      />
      <Route
        path="/new-business/*"
        element={
          <RequireAuth>
            <RequireCapability capability={CAP_NEW_BUSINESS}>
              <Suspense fallback={<PageLoader />}>
                <NewBusinessRoutes />
              </Suspense>
            </RequireCapability>
          </RequireAuth>
        }
      />
      <Route
        path="/product-owner/*"
        element={
          <RequireAuth>
            <RequireCapability capability={CAP_PRODUCT_OWNER}>
              <Suspense fallback={<PageLoader />}>
                <ProductOwnerRoutes />
              </Suspense>
            </RequireCapability>
          </RequireAuth>
        }
      />
      <Route
        path="/admin/*"
        element={
          <RequireAuth>
            <RequireCapability capability={CAP_ADMIN}>
              <Suspense fallback={<PageLoader />}>
                <AdministratorRoutes />
              </Suspense>
            </RequireCapability>
          </RequireAuth>
        }
      />
      {/*
        BLUF-1395 — Claim Administrator module. Single-page surface
        (read-only Policy Search + read-only Policy details view).
        Gated on `claim-admin-view` — held only by the
        `ClaimAdministrators` Cognito group. Server-side gate on the
        underlying policy-search endpoint is the authoritative check
        (`policies:read:all` at requireCap in `policies/plugin.ts`).
      */}
      <Route
        path="/claim-admin/*"
        element={
          <RequireAuth>
            <RequireCapability capability={CAP_CLAIM_ADMIN}>
              <Suspense fallback={<PageLoader />}>
                <ClaimAdminRoutes />
              </Suspense>
            </RequireCapability>
          </RequireAuth>
        }
      />
      <Route
        path="/quote/*"
        element={
          <RequireAuth>
            <Suspense fallback={<PageLoader />}>
              <QuoterRoutes />
            </Suspense>
          </RequireAuth>
        }
      />
    </Routes>
  );
}

export default App;
